import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  FileSignature,
  Loader2,
  UploadCloud,
  CheckCircle2,
  Download,
  CalendarCheck2,
} from 'lucide-react';
import {
  fetchDocuments,
  fetchDocumentRequests,
  uploadDocument,
  downloadInvoicePdf,
  fetchConvocations,
  downloadConvocationPdf,
} from '../api/candidate';
import { formatEuro, statusBadge } from '../lib/format';
import dayjs from '../lib/dates';
import type { CandidateDocumentRequest } from '../types';
import PendingSignatures from '../components/PendingSignatures';

const ACCEPTED_UPLOAD =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function Badge({ status }: { status: string }) {
  const { label, className } = statusBadge(status);
  return (
    <span
      className={`px-2.5 py-1 rounded-full border text-xs font-semibold flex-shrink-0 ${className}`}
    >
      {label}
    </span>
  );
}

function RequestRow({ req }: { req: CandidateDocumentRequest }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadDocument(req.id, file),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({
        queryKey: ['candidate', 'document-requests'],
      });
    },
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: unknown }).message)
          : "Échec de l'envoi.";
      setError(msg);
    },
  });

  const provided = req.status === 'provided';

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-sélectionner le même fichier
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Fichier trop volumineux (max 10 Mo).');
      return;
    }
    upload.mutate(file);
  };

  return (
    <div className="py-3.5 first:pt-0">
      <div className="flex items-center gap-4">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            provided ? 'bg-emerald-50' : 'bg-amber-50'
          }`}
        >
          {provided ? (
            <CheckCircle2 className="text-emerald-600" size={16} />
          ) : (
            <FileText className="text-amber-600" size={16} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{req.label}</div>
          {req.note ? (
            <div className="text-xs text-slate-500">{req.note}</div>
          ) : (
            <div className="text-xs text-slate-400">
              Demandé le {dayjs(req.requestedAt).format('DD/MM/YYYY')}
            </div>
          )}
          {provided && req.fileName && (
            <div className="text-xs text-emerald-600 mt-0.5 truncate">
              ✓ Envoyé : {req.fileName}
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_UPLOAD}
          className="hidden"
          onChange={onPick}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50 flex-shrink-0 ${
            provided
              ? 'text-slate-600 border border-slate-200 hover:bg-slate-50'
              : 'text-indigo-600 border border-indigo-200 hover:bg-indigo-50'
          }`}
        >
          {upload.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <UploadCloud size={14} />
          )}
          {provided ? 'Remplacer' : 'Envoyer le document'}
        </button>
      </div>
      {error && (
        <div className="text-xs text-red-600 mt-1 ml-[52px]">{error}</div>
      )}
    </div>
  );
}

function RequestedDocuments() {
  const { data, isLoading } = useQuery({
    queryKey: ['candidate', 'document-requests'],
    queryFn: fetchDocumentRequests,
  });

  const requests = data ?? [];
  if (isLoading || requests.length === 0) return null;

  const pending = requests.filter((r) => r.status === 'requested');

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-slate-900">Documents demandés</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        {pending.length > 0
          ? `Votre auto-école attend ${pending.length} document${
              pending.length > 1 ? 's' : ''
            }. Envoyez une photo ou un PDF (max 10 Mo).`
          : 'Tous les documents demandés ont été transmis.'}
      </p>
      <div className="divide-y divide-slate-100">
        {requests.map((req) => (
          <RequestRow key={req.id} req={req} />
        ))}
      </div>
    </div>
  );
}

/** Récapitulatifs de convocation à l'examen envoyés par l'auto-école (les plus récents d'abord). */
function ExamConvocations() {
  const { data } = useQuery({
    queryKey: ['candidate', 'convocations'],
    queryFn: fetchConvocations,
  });
  const [failed, setFailed] = useState(false);
  const convocations = data ?? [];
  if (convocations.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <h2 className="text-slate-900 mb-1">Convocations aux examens</h2>
      <p className="text-sm text-slate-500 mb-4">
        Récapitulatifs envoyés par votre auto-école. La convocation officielle
        vous est envoyée par e-mail par RdvPermis : elle fait foi.
      </p>
      {failed && (
        <p role="alert" className="text-sm text-red-600 mb-3">
          Le téléchargement a échoué. Réessayez.
        </p>
      )}
      <div className="divide-y divide-slate-100">
        {convocations.map((c) => (
          <div key={c.id} className="flex items-center gap-4 py-3.5 first:pt-0">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <CalendarCheck2 className="text-indigo-600" size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">
                Examen du {dayjs(c.examStartsAt).format('DD/MM/YYYY à HH:mm')}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {c.center} · reçu le {dayjs(c.sentAt).format('DD/MM/YYYY')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setFailed(false);
                downloadConvocationPdf(c).catch(() => setFailed(true));
              }}
              aria-label={`Télécharger la convocation de l’examen du ${dayjs(c.examStartsAt).format('DD/MM/YYYY')}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg px-2.5 py-1.5 hover:bg-indigo-50 flex-shrink-0"
            >
              <Download size={14} />
              PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DocumentsView() {
  const { data, isLoading } = useQuery({
    queryKey: ['candidate', 'documents'],
    queryFn: fetchDocuments,
  });

  const invoices = data?.invoices ?? [];
  const contracts = data?.contracts ?? [];
  const empty = !isLoading && invoices.length === 0 && contracts.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-slate-900">Mes documents</h1>
        <p className="text-slate-600">
          Vos factures, contrats de formation et convocations aux examens.
        </p>
      </div>

      <PendingSignatures />

      <RequestedDocuments />

      <ExamConvocations />

      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex items-center text-slate-500 text-sm">
          <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
        </div>
      ) : empty ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 shadow-sm text-center">
          <p className="text-sm text-slate-500">
            Aucun document disponible pour le moment.
          </p>
        </div>
      ) : (
        <>
          {/* Factures */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-slate-900 mb-4">Factures</h2>
            {invoices.length === 0 ? (
              <p className="text-sm text-slate-500">Aucune facture.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center gap-4 py-3.5 first:pt-0"
                  >
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="text-indigo-600" size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {inv.title}
                      </div>
                      <div className="text-xs text-slate-500">
                        {inv.number} · {dayjs(inv.issueDate).format('DD/MM/YYYY')}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 flex-shrink-0">
                      {formatEuro(inv.totalTtc)}
                    </div>
                    <Badge status={inv.status} />
                    <button
                      type="button"
                      onClick={() => downloadInvoicePdf(inv.id, inv.number)}
                      title="Télécharger la facture"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg px-2.5 py-1.5 hover:bg-indigo-50 flex-shrink-0"
                    >
                      <Download size={14} />
                      PDF
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contrats */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-slate-900 mb-4">Contrats</h2>
            {contracts.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun contrat.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {contracts.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-4 py-3.5 first:pt-0"
                  >
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <FileSignature className="text-indigo-600" size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        Contrat {c.number}
                      </div>
                      <div className="text-xs text-slate-500">
                        {c.signedAt
                          ? `Signé le ${dayjs(c.signedAt).format('DD/MM/YYYY')}`
                          : 'Non signé'}
                      </div>
                    </div>
                    <Badge status={c.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
